import React from 'react';
import { View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity, RefreshControl } from 'react-native';
import { platformAlert } from '../utils/platformAlert';
import { useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../types/navigation';
import { useColors, ThemeColors } from '../hooks/useColors';
import { supabase } from '../lib/supabase';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export default function VendorRegistrationScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [formData, setFormData] = useState({
    storeName: '',
    description: '',
  });

  const { data: session, refetch: refetchSession } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session;
    },
  });

  // Handle pull-to-refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    console.log('[VendorRegistrationScreen] Pull-to-refresh triggered');
    
    try {
      await refetchSession();
      console.log('[VendorRegistrationScreen] Pull-to-refresh completed successfully');
    } catch (error) {
      console.error('[VendorRegistrationScreen] Error during pull-to-refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const MAX_STORE_NAME_LENGTH = 50;
  const MAX_DESCRIPTION_LENGTH = 200;

  const validateForm = () => {
    if (!formData.storeName.trim()) {
      platformAlert('Error', 'Please enter a store name');
      return false;
    }
    if (formData.storeName.trim().length > MAX_STORE_NAME_LENGTH) {
      platformAlert('Error', `Store name must be ${MAX_STORE_NAME_LENGTH} characters or less`);
      return false;
    }
    if (!formData.description.trim()) {
      platformAlert('Error', 'Please enter a store description');
      return false;
    }
    if (formData.description.trim().length > MAX_DESCRIPTION_LENGTH) {
      platformAlert('Error', `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!session?.user?.id) {
      platformAlert('Error', 'Please sign in to create a store');
      return;
    }

    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      // Check if user already has any active (non-deleted) vendor stores
      const { data: existingVendors, error: checkError } = await supabase
        .from('vendors')
        .select('id, store_name, stripe_account_status')
        .eq('user_id', session.user.id)
        .eq('status', 'active')
        .not('is_deleted', 'eq', true);

      if (checkError) {
        console.error('Error checking existing vendors:', checkError);
        throw checkError;
      }

      if (existingVendors && existingVendors.length > 0) {
        console.log('[VendorRegistration] User already has vendors:', existingVendors);
        
        // Invalidate queries so the UI updates properly - SellScreen will show ProductListingScreen
        await queryClient.invalidateQueries({ queryKey: ['vendor-check'] });
        await queryClient.invalidateQueries({ queryKey: ['vendor-profile'] });
        
        // Silently navigate to VendorSettings to complete setup
        // This handles edge cases like incomplete account deletion where vendor record remains
        console.log('[VendorRegistration] Redirecting to VendorSettings for existing vendor');
        navigation.navigate('VendorSettings' as never);
        return;
      }

      // Default store hours: 9 AM to 5 PM, 7 days a week
      const defaultStoreHours = {
        monday: { isOpen: true, openTime: '09:00', closeTime: '17:00', is24Hours: false },
        tuesday: { isOpen: true, openTime: '09:00', closeTime: '17:00', is24Hours: false },
        wednesday: { isOpen: true, openTime: '09:00', closeTime: '17:00', is24Hours: false },
        thursday: { isOpen: true, openTime: '09:00', closeTime: '17:00', is24Hours: false },
        friday: { isOpen: true, openTime: '09:00', closeTime: '17:00', is24Hours: false },
        saturday: { isOpen: true, openTime: '09:00', closeTime: '17:00', is24Hours: false },
        sunday: { isOpen: true, openTime: '09:00', closeTime: '17:00', is24Hours: false },
        is24_7: false,
      };

      // Default metadata with order settings
      const defaultMetadata = {
        auto_accept_orders: false,  // Default to manual order review
      };

      // Create the vendor record with only required fields
      const { data: newVendor, error } = await supabase
        .from('vendors')
        .insert({
          user_id: session.user.id,
          store_name: formData.storeName.trim(),
          description: formData.description.trim(),
          primary_color: '#00A86B',  // Default primary color
          accent_color: '#00D488',   // Default accent color (Jade Green bright variant)
          status: 'active',
          delivery_hours: defaultStoreHours,  // Default store hours (9 AM - 5 PM, 7 days)
          metadata: defaultMetadata,  // Default order settings
        })
        .select()
        .single();

      if (error) throw error;

      console.log('Created vendor successfully:', newVendor);
      
      // IMPORTANT: Navigate to VendorSettings FIRST, before invalidating queries
      // This prevents SellScreen from switching to ProductListingScreen (which shows Stripe setup)
      // and interrupting our navigation flow
      navigation.navigate('VendorSettings' as never);
      
      // Now invalidate queries in the background (after navigation has started)
      // This ensures when user eventually goes back to SellScreen, it shows ProductListingScreen
      queryClient.invalidateQueries({ queryKey: ['vendor-check'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-profile'] });
    } catch (error: any) {
      console.error('Error creating vendor:', error);
      platformAlert(
        'Error',
        error.message || 'An error occurred while creating your store. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView 
      style={styles.container}
      refreshControl={
        <RefreshControl
          refreshing={isRefreshing}
          onRefresh={handleRefresh}
          tintColor={colors.primary}
          colors={[colors.primary]}
        />
      }
    >
      <View style={styles.content}>
        <Text style={styles.title}>Create Your Store</Text>
        
        <View style={styles.form}>
          <View style={styles.inputGroup}>
            <View style={styles.labelRow}>
              <Text style={styles.label}>Store Name</Text>
              <Text style={styles.characterCount}>
                {formData.storeName.length}/{MAX_STORE_NAME_LENGTH}
              </Text>
            </View>
            <TextInput
              style={styles.input}
              value={formData.storeName}
              onChangeText={(text) => setFormData(prev => ({ ...prev, storeName: text }))}
              placeholder="Your store name"
              placeholderTextColor={colors.textLight}
              maxLength={MAX_STORE_NAME_LENGTH}
            />
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.labelRow}>
              <Text style={styles.label}>Description</Text>
              <Text style={styles.characterCount}>
                {formData.description.length}/{MAX_DESCRIPTION_LENGTH}
              </Text>
            </View>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.description}
              onChangeText={(text) => setFormData(prev => ({ ...prev, description: text }))}
              placeholder="Describe your store"
              placeholderTextColor={colors.textLight}
              multiline
              numberOfLines={4}
              maxLength={MAX_DESCRIPTION_LENGTH}
            />
          </View>

          <TouchableOpacity
            style={[styles.submitButton, loading && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={loading}
          >
            <Text style={styles.submitButtonText}>
              {loading ? 'Creating Store...' : 'Create Store'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    padding: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    marginBottom: 24,
    textAlign: 'center',
    color: colors.text,
  },
  form: {
    gap: 16,
  },
  inputGroup: {
    gap: 8,
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.text,
  },
  characterCount: {
    fontSize: 12,
    color: colors.textLight,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: colors.text,
    backgroundColor: colors.surface,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  submitButton: {
    backgroundColor: colors.primary,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
  },
  submitButtonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: colors.surface,
    fontSize: 18,
    fontWeight: '600',
  },
}); 